/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Copy, Mail, TestTube2 } from 'lucide-react';

const email = 'koemori@meetune.com';

export default function Download() {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2600);
    } catch {
      window.location.href = `mailto:${email}?subject=${encodeURIComponent('クローズドベータテスト応募')}`;
    }
  };

  return (
    <section id="download-content" className="py-20 lg:pt-8 lg:pb-10 bg-[#080A0A] relative overflow-hidden">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[#00F5D4]/6 blur-[145px]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#5646FF]/6 blur-[120px]" />

      <div className="w-full max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-8 lg:mb-6">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-4 lg:mb-2"
          >
            DOWNLOAD
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55 }}
            className="text-3xl md:text-5xl font-black tracking-tight text-white mb-5 lg:mb-3"
          >
            配信開始！
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="text-base md:text-lg text-white/55 leading-8 lg:leading-7"
          >
            iOS / Androidにて配信中！ 高品質な伴奏と独自のボーカル補正で、新しい歌声の体験をお届けします。さっそくアプリをダウンロードして歌おう！
          </motion.p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <motion.article
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.58 }}
            className="lg:col-span-7 p-7 md:p-10 lg:p-7 rounded-[30px] bg-gradient-to-br from-[#151A19] to-[#101312] border border-white/[0.08] shadow-[0_24px_60px_rgba(0,0,0,0.35)] flex flex-col justify-between relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-[#00F5D4]/8 blur-[80px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] text-[#00F5D4] mb-5">
                <span className="w-8 h-px bg-[#00F5D4]" />
                OFFICIAL RELEASE
              </div>
              <h3 className="text-3xl md:text-4xl font-black text-white mb-5 lg:mb-3">koemoriへようこそ！</h3>
              <p className="text-sm md:text-base text-white/55 leading-7 md:leading-8 lg:leading-7 mb-9 lg:mb-5">
                豊富な伴奏、手軽に楽しめるサビカラ、みんなで歌声を重ねる弾幕コラボなど、ここでしか味わえない音楽体験を今すぐ始めましょう。
              </p>
            </div>

            <div className="relative">
              <div className="flex items-center gap-2 text-xs font-bold text-white/42 mb-3">
                <TestTube2 className="w-4 h-4 text-[#00F5D4]" />
                お問い合わせ・サポート
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="group w-full flex items-center gap-3 p-2 pl-4 rounded-2xl bg-black/30 border border-white/[0.08] hover:border-[#00F5D4]/35 transition-all cursor-pointer text-left"
                aria-label="お問い合わせ・サポートをコピー"
              >
                <Mail className="w-5 h-5 text-[#00F5D4] shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm md:text-base font-bold text-white">
                  {email}
                </span>
                <span className="h-11 min-w-[126px] shrink-0 whitespace-nowrap px-4 rounded-xl bg-[#00F5D4]/10 border border-[#00F5D4]/15 text-[#00F5D4] flex items-center justify-center gap-2 text-xs leading-none font-bold group-hover:bg-[#00F5D4] group-hover:text-[#04110F] transition-all">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'コピー済み' : 'コピー'}
                </span>
              </button>
              <p className="text-[11px] text-white/32 mt-3 leading-5">
                ※不具合のご報告や楽曲リクエストもお気軽にお寄せください。
              </p>
              <AnimatePresence>
                {copied && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="mt-2 text-[11px] text-[#00F5D4]"
                  >
                    メールアドレスをコピーしました
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.58, delay: 0.08 }}
            className="lg:col-span-5 p-7 md:p-8 lg:p-7 rounded-[30px] bg-gradient-to-br from-[#151A19] to-[#101312] border border-white/[0.08] shadow-[0_24px_60px_rgba(0,0,0,0.35)] flex flex-col"
          >
            <p className="text-sm font-bold text-white mb-5 lg:mb-4">対応プラットフォーム</p>

            <div className="space-y-4 lg:space-y-3">
              <a
                href="https://apps.apple.com/jp/app/koemori/id6801087619"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 p-4 rounded-2xl bg-white/[0.045] border border-white/[0.07] hover:-translate-y-0.5 hover:border-white/20 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-black/35 flex items-center justify-center shrink-0">
                  <img src="/apple-icon.webp" alt="Apple" className="w-7 h-7 object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-bold text-white">App Store</p>
                </div>
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.meetune.koemori"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 p-4 rounded-2xl bg-white/[0.045] border border-white/[0.07] hover:-translate-y-0.5 hover:border-white/20 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-black/35 flex items-center justify-center shrink-0">
                  <img src="/android-icon.webp" alt="Android" className="w-8 h-8 object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-bold text-white">Google Play</p>
                </div>
              </a>
            </div>

            <div className="mt-auto pt-7">
              <div className="h-px bg-white/[0.07] mb-5" />
              <div className="flex items-center gap-3 text-white/35 mb-4" aria-label="iOS・Google Play">
                <span className="text-xs font-bold">iOS</span>
                <span className="w-1 h-1 rounded-full bg-[#00F5D4]" />
                <span className="text-xs font-bold">Google Play</span>
              </div>
              <p className="text-[11px] leading-5 text-white/32">
                バージョン 1.0.0｜iOS 12.0以降／Android 8.0以降
              </p>
            </div>
          </motion.article>
        </div>
      </div>

    </section>
  );
}
